import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MobileModal from './MobileModal';
import { getAuthHeaders } from '../utils/api';
import { getSeatCountForBoatClass } from '../utils/boatSeats';
import { tracksMeters } from '../constants/boatClasses';
import { trainingLogDistanceField } from '../utils/distanceInput';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const MODALITIES = ['Run', 'Bike', 'Erg', 'Row', 'Strength', 'Recovery', 'Other'];

const MODALITY_ICONS = {
  Run: '🏃',
  Bike: '🚴',
  Erg: '⚙️',
  Row: '🚣',
  Strength: '💪',
  Recovery: '🧘',
  Other: '🏋️'
};

const MODALITY_COLORS = {
  Run: 'bg-green-100 text-green-800 border-green-200',
  Bike: 'bg-blue-100 text-blue-800 border-blue-200',
  Erg: 'bg-orange-100 text-orange-800 border-orange-200',
  Row: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  Strength: 'bg-red-100 text-red-800 border-red-200',
  Recovery: 'bg-pink-100 text-pink-800 border-pink-200',
  Other: 'bg-purple-100 text-purple-800 border-purple-200'
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [month, day, year] = dateStr.split('/');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatMinutes(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatDistance(meters) {
  if (!meters) return null;
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)}km`;
  }
  return `${meters}m`;
}

function sumZoneMinutes(zoneValues = {}) {
  return Object.values(zoneValues).reduce((sum, value) => {
    const parsed = parseInt(value, 10);
    return sum + (isNaN(parsed) ? 0 : parsed);
  }, 0);
}

export default function TrainingLog() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Rankings state
  const [rankings, setRankings] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [lookbackDate, setLookbackDate] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [leaderboardMetric, setLeaderboardMetric] = useState('minutes'); // 'minutes' | 'onWaterKm'
  const [onWaterRankings, setOnWaterRankings] = useState([]);
  const [userOnWaterRank, setUserOnWaterRank] = useState(null);
  const [userOnWaterStats, setUserOnWaterStats] = useState(null);
  const [onWaterTotalParticipants, setOnWaterTotalParticipants] = useState(0);

  // Form state
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [minutes, setMinutes] = useState('');
  const [modality, setModality] = useState('');
  const [distance, setDistance] = useState('');
  const [distanceUnit, setDistanceUnit] = useState('meters'); // 'meters' or 'km'
  const [notes, setNotes] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  const [zoneLoggingEnabled, setZoneLoggingEnabled] = useState(false);
  const [zoneDefinitions, setZoneDefinitions] = useState([]);
  const [stravaDefaultZoneKey, setStravaDefaultZoneKey] = useState('');
  const [intendedZones, setIntendedZones] = useState({});
  const [actualZones, setActualZones] = useState({});
  const [planEntries, setPlanEntries] = useState([]); // team plan sessions for the selected date
  const [appliedPlanEntryId, setAppliedPlanEntryId] = useState(null);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [existingLogs, setExistingLogs] = useState([]);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  // { kind: 'duplicate' | 'overlap', lines, retry } when the server answers 409 and needs confirmation
  const [saveConflict, setSaveConflict] = useState(null);
  const [onWaterPeriod, setOnWaterPeriod] = useState('year'); // week | month | year
  const [onWaterKm, setOnWaterKm] = useState(0);

  const [clubMembersForMeters, setClubMembersForMeters] = useState([]);
  const [meterCreditUserIds, setMeterCreditUserIds] = useState([]);
  const [showMeterCreditSelector, setShowMeterCreditSelector] = useState(false);

  // Filter state
  const [filterModality, setFilterModality] = useState('');
  const [dateRange, setDateRange] = useState('all'); // 'week', 'month', 'all'

  useEffect(() => {
    fetchLogs();
  }, [filterModality, dateRange]);

  // Fetch equipment when token is available
  useEffect(() => {
    if (token) {
      fetchEquipment();
      fetchZoneSettings();
    }
  }, [token]);

  useEffect(() => {
    fetchRankings();
  }, [selectedTeams]);

  useEffect(() => {
    fetchOnWaterTotal();
  }, [token, onWaterPeriod]);

  useEffect(() => {
    if (!showAddForm || !token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/boats/meters/club-members`, {
          headers: getAuthHeaders(token)
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setClubMembersForMeters(data.users || []);
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddForm, token]);

  useEffect(() => {
    setEquipmentSearch('');
    setShowEquipmentDropdown(false);
    if (modality !== 'Erg' && modality !== 'Row') {
      setMeterCreditUserIds([]);
      setShowMeterCreditSelector(false);
    }
  }, [modality]);

  // Fetch the team plan for the selected date (prefill source). Only for new
  // logs; the backend returns an empty list when the feature flag is off.
  useEffect(() => {
    if (!showAddForm || editingLog || !token || !logDate) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [year, month, day] = logDate.split('-');
        const formattedDate = `${month}/${day}/${year}`;
        const response = await fetch(
          `${API_URL}/api/training-plans/for-date?date=${encodeURIComponent(formattedDate)}`,
          { headers: getAuthHeaders(token) }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const entries = data.enabled ? (data.entries || []) : [];
        setPlanEntries(entries);
        setAppliedPlanEntryId(null);
        // Auto-apply a single planned session when the form is still untouched.
        if (entries.length === 1 && !modality) {
          applyPlanEntry(entries[0]);
        }
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm, editingLog, token, logDate]);

  const applyPlanEntry = (entry) => {
    setModality(entry.modality);
    if (zoneLoggingEnabled && entry.plannedZones) {
      const nextIntended = {};
      Object.entries(entry.plannedZones).forEach(([key, value]) => {
        nextIntended[key] = String(value);
      });
      setIntendedZones(nextIntended);
    } else if (entry.plannedMinutes) {
      setMinutes(String(entry.plannedMinutes));
    }
    setAppliedPlanEntryId(entry.id);
  };

  const planEntrySummary = (entry) => {
    const zoneBits = entry.plannedZones
      ? Object.entries(entry.plannedZones).map(([key, value]) => {
          const label = zoneDefinitions.find(z => z.key === key)?.label || key;
          return `${label} ${value}'`;
        })
      : [];
    return `${entry.modality} ${entry.plannedMinutes || 0}'${zoneBits.length ? ` (${zoneBits.join(', ')})` : ''}`;
  };

  useEffect(() => {
    if (!selectedEquipment || !equipmentList.length) return;
    const eq = equipmentList.find((e) => String(e.id) === String(selectedEquipment));
    if (!eq) return;
    const cap = getSeatCountForBoatClass(eq.boat_class);
    setMeterCreditUserIds((prev) => (prev.length > cap ? prev.slice(0, cap) : prev));
  }, [selectedEquipment, equipmentList]);

  const fetchEquipment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/boats`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to show ergs and boats that can have meters logged
        setEquipmentList(data.boats || []);
      }
    } catch (err) {
      console.error('Error fetching equipment:', err);
    }
  };

  const fetchZoneSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setZoneLoggingEnabled(Boolean(data?.settings?.zoneLoggingEnabled));
        const defs = Array.isArray(data?.settings?.zoneDefinitions) ? data.settings.zoneDefinitions : [];
        setZoneDefinitions(defs);
        setStravaDefaultZoneKey(data?.settings?.stravaDefaultZoneKey || defs[0]?.key || '');
      }
    } catch (err) {
      console.error('Error fetching zone logging settings:', err);
    }
  };

  const fetchOnWaterTotal = async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/boats/meters/on-water-total?period=${encodeURIComponent(onWaterPeriod)}`,
        { headers: getAuthHeaders(token) }
      );
      if (!response.ok) return;
      const data = await response.json();
      setOnWaterKm(Number(data.totalKm) || 0);
    } catch (err) {
      console.error('Error fetching on-water total:', err);
    }
  };

  const buildZonePayload = (zoneValues) => {
    const payload = {};
    zoneDefinitions.forEach(zone => {
      const raw = zoneValues?.[zone.key];
      if (raw !== undefined && raw !== null && raw !== '') {
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          payload[zone.key] = parsed;
        }
      }
    });
    return Object.keys(payload).length > 0 ? payload : null;
  };

  const toggleTeamFilter = (team) => {
    setSelectedTeams(prev => 
      prev.includes(team) 
        ? prev.filter(t => t !== team)
        : [...prev, team]
    );
  };

  const fetchRankings = async () => {
    try {
      let url = `${API_URL}/api/training-logs/rankings`;
      if (selectedTeams.length > 0) {
        url += `?teams=${encodeURIComponent(selectedTeams.join(','))}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setRankings(data.rankings || []);
        setUserRank(data.userRank);
        setUserStats(data.userStats);
        setTotalParticipants(data.totalParticipants);
        setOnWaterRankings(data.onWaterRankings || []);
        setUserOnWaterRank(data.userOnWaterRank ?? null);
        setUserOnWaterStats(data.userOnWaterStats || null);
        setOnWaterTotalParticipants(data.onWaterTotalParticipants ?? 0);
        setLookbackDate(data.lookbackDate);
        if (data.availableTeams) {
          setAvailableTeams(data.availableTeams);
        }
      }
    } catch (err) {
      console.error('Error fetching rankings:', err);
    }
  };

  const getDateRangeParams = () => {
    const today = new Date();
    let startDate = null;

    if (dateRange === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      startDate = `${String(weekAgo.getMonth() + 1).padStart(2, '0')}/${String(weekAgo.getDate()).padStart(2, '0')}/${weekAgo.getFullYear()}`;
    } else if (dateRange === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      startDate = `${String(monthAgo.getMonth() + 1).padStart(2, '0')}/${String(monthAgo.getDate()).padStart(2, '0')}/${monthAgo.getFullYear()}`;
    }

    return startDate;
  };

  const fetchLogs = async () => {
    try {
      let url = `${API_URL}/api/training-logs/my`;
      const params = new URLSearchParams();

      const startDate = getDateRangeParams();
      if (startDate) params.append('startDate', startDate);
      if (filterModality) params.append('modality', filterModality);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        console.error('Failed to fetch training logs');
      }
    } catch (err) {
      console.error('Error fetching training logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitTrainingLog = async (submission, skipBoatTotal = false, acknowledgeAssigneeOverlap = false, acknowledgeDuplicate = false) => {
    try {
      const { url, method, payload } = submission;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...payload,
          skipBoatTotal,
          ...(acknowledgeAssigneeOverlap ? { acknowledgeAssigneeOverlap: true } : {}),
          ...(acknowledgeDuplicate ? { acknowledgeDuplicate: true } : {})
        })
      });

      if (response.ok) {
        setPendingSubmission(null);
        setShowDuplicatePrompt(false);
        setExistingLogs([]);
        if (submission.addAnother) {
          clearForNextEntry();
          setSuccess('Saved. Ready for another entry.');
        } else {
          setSuccess('Training log saved.');
          resetForm();
        }
        fetchLogs();
        fetchRankings(); // Refresh rankings after logging
      } else {
        const data = await response.json().catch(() => ({}));
        if (
          response.status === 409 &&
          data.code === 'ASSIGNEE_OVERLAP' &&
          data.assigneeOverlap?.length &&
          !acknowledgeAssigneeOverlap
        ) {
          setSaveConflict({
            kind: 'overlap',
            lines: data.assigneeOverlap.map(
              (row) => `${row.name}: ${row.entries.map((e) => `${e.meters}m (${e.source})`).join(', ')}`
            ),
            retry: () => submitTrainingLog(submission, skipBoatTotal, true, acknowledgeDuplicate)
          });
          return;
        }
        if (
          response.status === 409 &&
          data.code === 'POSSIBLE_DUPLICATE' &&
          !acknowledgeDuplicate
        ) {
          setSaveConflict({
            kind: 'duplicate',
            lines: (data.duplicates || []).map(
              (d) => `${formatMinutes(d.minutes)}${d.distanceMeters ? ` / ${formatDistance(d.distanceMeters)}` : ''}${d.notes ? ` — ${d.notes.split('\n')[0]}` : ''}`
            ),
            retry: () => submitTrainingLog(submission, skipBoatTotal, acknowledgeAssigneeOverlap, true)
          });
          return;
        }
        setError(data.error || 'Failed to save training log');
      }
    } catch (err) {
      console.error('Error saving training log:', err);
      setError('Failed to save training log');
    }
  };

  const confirmSaveConflict = () => {
    const conflict = saveConflict;
    setSaveConflict(null);
    conflict?.retry();
  };

  const dismissSaveConflict = () => {
    const conflict = saveConflict;
    setSaveConflict(null);
    setError(
      conflict?.kind === 'duplicate'
        ? 'Not saved. That session is already in your log from Strava. Your entry is still here if you want to change it.'
        : 'Not saved. Change who gets meter credit, or save again and confirm.'
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!logDate || !modality) {
      setError('Please fill in all required fields');
      return;
    }
    if (zoneLoggingEnabled && !editingLog && sumZoneMinutes(actualZones) <= 0) {
      setError('Please enter actual zone minutes greater than 0');
      return;
    }

    // Convert date from YYYY-MM-DD to MM/DD/YYYY
    const [year, month, day] = logDate.split('-');
    const formattedDate = `${month}/${day}/${year}`;

    // Convert distance to meters
    let distanceMeters = null;
    if (distance) {
      distanceMeters = distanceUnit === 'km' 
        ? Math.round(parseFloat(distance) * 1000) 
        : parseInt(distance);
    }

    if (selectedEquipment && (!distanceMeters || distanceMeters <= 0)) {
      setError('Enter a positive distance when selecting equipment');
      return;
    }

    const submission = {
      url: editingLog
        ? `${API_URL}/api/training-logs/${editingLog.id}`
        : `${API_URL}/api/training-logs`,
      method: editingLog ? 'PUT' : 'POST',
      addAnother: false,
      payload: {
        logDate: formattedDate,
        minutes: zoneLoggingEnabled
          ? sumZoneMinutes(buildZonePayload(actualZones) || {})
          : parseInt(minutes, 10),
        modality,
        distanceMeters,
        notes: notes || null,
        equipmentId: selectedEquipment ? parseInt(selectedEquipment, 10) : null,
        intendedZones: zoneLoggingEnabled ? buildZonePayload(intendedZones) : null,
        actualZones: zoneLoggingEnabled ? buildZonePayload(actualZones) : null,
        ...(editingLog ? {} : { planEntryId: appliedPlanEntryId }),
        ...(selectedEquipment && meterCreditUserIds.length > 0
          ? {
              assignedUserIds: meterCreditUserIds
                .map((x) => parseInt(x, 10))
                .filter((n) => !isNaN(n) && n > 0)
            }
          : {})
      }
    };

    const selectedEquipmentId = selectedEquipment ? parseInt(selectedEquipment, 10) : null;
    const originalEquipmentId = editingLog?.equipment_id ? parseInt(editingLog.equipment_id, 10) : null;
    const willCreateEquipmentMeterWrite = Boolean(
      selectedEquipmentId &&
      distanceMeters > 0 &&
      (!editingLog || selectedEquipmentId !== originalEquipmentId)
    );

    // Check duplicate day entries before any action that would create a new equipment meter write.
    if (willCreateEquipmentMeterWrite) {
      try {
        const checkParams = new URLSearchParams({
          boatId: String(selectedEquipmentId),
          date: formattedDate
        });
        if (meterCreditUserIds.length > 0) {
          checkParams.set('userIds', meterCreditUserIds.join(','));
        }
        const checkResponse = await fetch(
          `${API_URL}/api/boats/meters/check?${checkParams.toString()}`,
          { headers: getAuthHeaders(token) }
        );
        const checkData = await checkResponse.json();

        if (checkData.hasExisting) {
          setPendingSubmission(submission);
          setExistingLogs(checkData.existingLogs || []);
          setShowDuplicatePrompt(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check for duplicates:', err);
        setError('Failed to verify duplicate meters. Please try again.');
        return;
      }
    }

    await submitTrainingLog(submission, false);
  };

  const handleSaveAndAddAnother = async () => {
    setError('');
    setSuccess('');

    if (editingLog) {
      setError('Save & Add Another is only available when creating a new log.');
      return;
    }

    const fakeEvent = { preventDefault: () => {} };
    // Reuse handleSubmit validation logic, then mark this submission as add-another.
    // We do this by re-building the submission payload here for explicit control.
    if (!logDate || !modality) {
      setError('Please fill in all required fields');
      return;
    }
    if (zoneLoggingEnabled && sumZoneMinutes(actualZones) <= 0) {
      setError('Please enter actual zone minutes greater than 0');
      return;
    }

    const [year, month, day] = logDate.split('-');
    const formattedDate = `${month}/${day}/${year}`;
    let distanceMeters = null;
    if (distance) {
      distanceMeters = distanceUnit === 'km'
        ? Math.round(parseFloat(distance) * 1000)
        : parseInt(distance, 10);
    }
    if (selectedEquipment && (!distanceMeters || distanceMeters <= 0)) {
      setError('Enter a positive distance when selecting equipment');
      return;
    }

    const selectedEquipmentId = selectedEquipment ? parseInt(selectedEquipment, 10) : null;
    const submission = {
      url: `${API_URL}/api/training-logs`,
      method: 'POST',
      addAnother: true,
      payload: {
        logDate: formattedDate,
        minutes: zoneLoggingEnabled
          ? sumZoneMinutes(buildZonePayload(actualZones) || {})
          : parseInt(minutes, 10),
        modality,
        distanceMeters,
        notes: notes || null,
        equipmentId: selectedEquipmentId,
        intendedZones: zoneLoggingEnabled ? buildZonePayload(intendedZones) : null,
        actualZones: zoneLoggingEnabled ? buildZonePayload(actualZones) : null,
        planEntryId: appliedPlanEntryId,
        ...(selectedEquipmentId && meterCreditUserIds.length > 0
          ? {
              assignedUserIds: meterCreditUserIds
                .map((x) => parseInt(x, 10))
                .filter((n) => !isNaN(n) && n > 0)
            }
          : {})
      }
    };

    if (selectedEquipmentId && distanceMeters > 0) {
      try {
        const checkParams = new URLSearchParams({
          boatId: String(selectedEquipmentId),
          date: formattedDate
        });
        if (meterCreditUserIds.length > 0) {
          checkParams.set('userIds', meterCreditUserIds.join(','));
        }
        const checkResponse = await fetch(
          `${API_URL}/api/boats/meters/check?${checkParams.toString()}`,
          { headers: getAuthHeaders(token) }
        );
        const checkData = await checkResponse.json();
        if (checkData.hasExisting) {
          setPendingSubmission(submission);
          setExistingLogs(checkData.existingLogs || []);
          setShowDuplicatePrompt(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check for duplicates:', err);
        setError('Failed to verify duplicate meters. Please try again.');
        return;
      }
    }

    await submitTrainingLog(submission, false);
    void fakeEvent;
  };

  const handleDuplicateChoice = async (isSameSession) => {
    if (!pendingSubmission) return;
    await submitTrainingLog(pendingSubmission, isSameSession);
  };

  const cancelDuplicatePrompt = () => {
    setShowDuplicatePrompt(false);
    setPendingSubmission(null);
    setExistingLogs([]);
  };

  const handleDelete = async (logId) => {
    if (!confirm('Are you sure you want to delete this training log?')) return;

    try {
      const response = await fetch(`${API_URL}/api/training-logs/${logId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchLogs();
        fetchRankings(); // Refresh rankings after deleting
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete training log');
      }
    } catch (err) {
      console.error('Error deleting training log:', err);
      setError('Failed to delete training log');
    }
  };

  const handleEdit = (log) => {
    // Convert MM/DD/YYYY to YYYY-MM-DD for the date input
    const [month, day, year] = log.log_date.split('/');
    const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    setLogDate(formattedDate);
    setMinutes(log.minutes.toString());
    setModality(log.modality);
    const distanceField = trainingLogDistanceField(log.distance_meters);
    setDistance(distanceField.distance);
    setDistanceUnit(distanceField.unit);
    setNotes(log.notes || '');
    setSelectedEquipment(log.equipment_id ? log.equipment_id.toString() : '');
    const creditIds = log.meter_credit_user_ids;
    if (Array.isArray(creditIds) && creditIds.length > 0) {
      setMeterCreditUserIds(creditIds.map(String));
    } else {
      setMeterCreditUserIds([]);
    }
    setShowMeterCreditSelector(false);
    const nextIntended = {};
    const nextActual = {};
    zoneDefinitions.forEach(zone => {
      if (log.intended_zones && log.intended_zones[zone.key] !== undefined) {
        nextIntended[zone.key] = String(log.intended_zones[zone.key]);
      }
      if (log.actual_zones && log.actual_zones[zone.key] !== undefined) {
        nextActual[zone.key] = String(log.actual_zones[zone.key]);
      }
    });
    const hasActualZones = Object.keys(nextActual).some((key) => parseInt(nextActual[key], 10) > 0);
    if (zoneLoggingEnabled && !hasActualZones && log.minutes > 0) {
      const fallbackKey = stravaDefaultZoneKey || zoneDefinitions[0]?.key;
      if (fallbackKey) {
        nextActual[fallbackKey] = String(log.minutes);
      }
    }
    setIntendedZones(nextIntended);
    setActualZones(nextActual);
    setEditingLog(log);
    setShowAddForm(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = parseInt(params.get('editTrainingLogId') || '', 10);
    if (!editId || logs.length === 0) return;
    const match = logs.find((l) => l.id === editId);
    if (match) {
      handleEdit(match);
      params.delete('editTrainingLogId');
      const next = params.toString();
      window.history.replaceState({}, '', next ? `?${next}` : window.location.pathname);
    }
  }, [logs]);

  const resetForm = () => {
    setLogDate(new Date().toISOString().split('T')[0]);
    setMinutes('');
    setModality('');
    setDistance('');
    setDistanceUnit('meters');
    setNotes('');
    setSelectedEquipment('');
    setIntendedZones({});
    setActualZones({});
    setMeterCreditUserIds([]);
    setShowMeterCreditSelector(false);
    setPlanEntries([]);
    setAppliedPlanEntryId(null);
    setEditingLog(null);
    setShowAddForm(false);
    setError('');
    setSuccess('');
  };

  const clearForNextEntry = () => {
    setMinutes('');
    setDistance('');
    setNotes('');
    setIntendedZones({});
    setActualZones({});
    setMeterCreditUserIds([]);
    setShowMeterCreditSelector(false);
    setAppliedPlanEntryId(null);
    setError('');
    setShowAddForm(true);
  };

  // Calculate totals
  const totalMinutes = logs.reduce((sum, log) => sum + log.minutes, 0);
  const totalByModality = logs.reduce((acc, log) => {
    acc[log.modality] = (acc[log.modality] || 0) + log.minutes;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center">
        <p className="text-gray-500">Loading training logs...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 p-2 sm:p-6 pb-24 sm:pb-6">
      {/* Left Sidebar - Leaderboard */}
      <div className="lg:w-72 lg:flex-shrink-0">
        <div className="lg:sticky lg:top-4">
          <div className="bg-gradient-to-b from-amber-50 to-orange-50 border border-amber-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
              <div className="flex items-center gap-2">
                <span className="text-xl">🏆</span>
                <span className="font-bold text-amber-800">Leaderboard</span>
              </div>
              <div className="flex mt-2 rounded-lg border border-amber-300/80 overflow-hidden bg-white/60 p-0.5">
                <button
                  type="button"
                  onClick={() => setLeaderboardMetric('minutes')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    leaderboardMetric === 'minutes'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-amber-800 hover:bg-amber-100/80'
                  }`}
                >
                  Minutes
                </button>
                <button
                  type="button"
                  onClick={() => setLeaderboardMetric('onWaterKm')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    leaderboardMetric === 'onWaterKm'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-amber-800 hover:bg-amber-100/80'
                  }`}
                >
                  On-water km
                </button>
              </div>
              {leaderboardMetric === 'minutes' && userRank != null && (
                <p className="text-sm text-amber-700 mt-1">
                  You're <span className="font-bold">#{userRank}</span> of {totalParticipants}
                </p>
              )}
              {leaderboardMetric === 'onWaterKm' && userOnWaterRank != null && (
                <p className="text-sm text-amber-700 mt-1">
                  You're <span className="font-bold">#{userOnWaterRank}</span> of {onWaterTotalParticipants}
                </p>
              )}
              {leaderboardMetric === 'onWaterKm' && userOnWaterRank == null && onWaterTotalParticipants > 0 && (
                <p className="text-sm text-amber-600 mt-1">No boat meters credited to you in this period yet</p>
              )}
            </div>

            {/* Team Filter */}
            {availableTeams.length > 0 && (
              <div className="px-3 py-2 border-b border-amber-200 bg-white/50">
                <p className="text-xs font-medium text-amber-700 mb-1.5">Filter by Team:</p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {availableTeams.map((team) => (
                    <label key={team} className="flex items-center gap-2 cursor-pointer hover:bg-amber-100/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedTeams.includes(team)}
                        onChange={() => toggleTeamFilter(team)}
                        className="w-3.5 h-3.5 text-amber-600 rounded border-amber-300 focus:ring-amber-400"
                      />
                      <span className="text-sm text-gray-700">{team}</span>
                    </label>
                  ))}
                </div>
                {selectedTeams.length > 0 && (
                  <button
                    onClick={() => setSelectedTeams([])}
                    className="text-xs text-amber-600 hover:text-amber-800 mt-1"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {/* Lookback Date */}
            {lookbackDate && (
              <div className="px-3 py-1.5 bg-white/50 border-b border-amber-200 text-xs text-amber-700">
                Since {new Date(lookbackDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}

            {/* Rankings List */}
            <div className="divide-y divide-amber-100 bg-white/80 max-h-[calc(100vh-300px)] lg:max-h-[calc(100vh-220px)] overflow-y-auto">
              {leaderboardMetric === 'minutes' && rankings.length === 0 && (
                <div className="px-3 py-4 text-center text-amber-700 text-sm">
                  No training logs yet
                </div>
              )}
              {leaderboardMetric === 'onWaterKm' && onWaterRankings.length === 0 && (
                <div className="px-3 py-4 text-center text-amber-700 text-sm">
                  No on-water meters in this period
                </div>
              )}
              {leaderboardMetric === 'minutes' && rankings.length > 0 && (() => {
                const maxMin = Math.max(...rankings.map((r) => r.totalMinutes || 0), 1);
                return rankings.map((r) => {
                  const pct = ((r.totalMinutes || 0) / maxMin) * 100;
                  return (
                  <div 
                    key={r.userId} 
                    className={`flex items-center gap-2 px-3 py-2 ${
                      r.isCurrentUser ? 'bg-amber-100' : ''
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs flex-shrink-0 ${
                      r.rank === 1 ? 'bg-amber-400 text-amber-900' :
                      r.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      r.rank === 3 ? 'bg-orange-300 text-orange-800' :
                      'bg-amber-200/50 text-amber-700'
                    }`}>
                      {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : r.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center gap-2 mb-0.5">
                        <p className={`text-sm font-medium truncate ${r.isCurrentUser ? 'text-amber-900' : 'text-gray-800'}`}>
                          {r.userName}
                          {r.isCurrentUser && <span className="text-xs ml-1 text-amber-600">(You)</span>}
                        </p>
                        <p className={`text-sm font-bold whitespace-nowrap ${r.isCurrentUser ? 'text-amber-900' : 'text-gray-800'}`}>
                          {formatMinutes(r.totalMinutes)}
                        </p>
                      </div>
                      {r.team && selectedTeams.length === 0 && (
                        <p className="text-xs text-gray-500 truncate">{r.team}</p>
                      )}
                      <div className="w-full bg-amber-100/80 rounded-full h-1.5 mt-1">
                        <div className="bg-amber-500 rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
                });
              })()}
              {leaderboardMetric === 'onWaterKm' && onWaterRankings.length > 0 && (() => {
                const maxM = Math.max(...onWaterRankings.map((r) => r.totalOnWaterMeters || 0), 1);
                return onWaterRankings.map((r) => {
                  const pct = ((r.totalOnWaterMeters || 0) / maxM) * 100;
                  return (
                  <div 
                    key={r.userId} 
                    className={`flex items-center gap-2 px-3 py-2 ${
                      r.isCurrentUser ? 'bg-amber-100' : ''
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs flex-shrink-0 ${
                      r.rank === 1 ? 'bg-amber-400 text-amber-900' :
                      r.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      r.rank === 3 ? 'bg-orange-300 text-orange-800' :
                      'bg-amber-200/50 text-amber-700'
                    }`}>
                      {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : r.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center gap-2 mb-0.5">
                        <p className={`text-sm font-medium truncate ${r.isCurrentUser ? 'text-amber-900' : 'text-gray-800'}`}>
                          {r.userName}
                          {r.isCurrentUser && <span className="text-xs ml-1 text-amber-600">(You)</span>}
                        </p>
                        <p className={`text-sm font-bold whitespace-nowrap ${r.isCurrentUser ? 'text-amber-900' : 'text-gray-800'}`}>
                          {r.totalOnWaterKm.toFixed(1)} km
                        </p>
                      </div>
                      {r.team && selectedTeams.length === 0 && (
                        <p className="text-xs text-gray-500 truncate">{r.team}</p>
                      )}
                      <div className="w-full bg-amber-100/80 rounded-full h-1.5 mt-1">
                        <div className="bg-cyan-600 rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
                });
              })()}
            </div>
            
            {/* Show user's position if not in top 10 */}
            {leaderboardMetric === 'minutes' && userRank && userRank > 10 && userStats && (
              <div className="border-t-2 border-dashed border-amber-300 bg-amber-100">
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-200/50 text-amber-700 font-bold text-xs flex-shrink-0">
                    {userRank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900 truncate">You</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-amber-900">{formatMinutes(userStats.totalMinutes)}</p>
                  </div>
                </div>
              </div>
            )}
            {leaderboardMetric === 'onWaterKm' && userOnWaterRank && userOnWaterRank > 10 && userOnWaterStats && (
              <div className="border-t-2 border-dashed border-amber-300 bg-amber-100">
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-200/50 text-amber-700 font-bold text-xs flex-shrink-0">
                    {userOnWaterRank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900 truncate">You</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-amber-900">{userOnWaterStats.totalOnWaterKm.toFixed(1)} km</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary">My Training Log</h2>
          <button
            type="button"
            onClick={() => {
              setEditingLog(null);
              setMeterCreditUserIds([]);
              setShowMeterCreditSelector(false);
              setError('');
              setSuccess('');
              setShowAddForm(true);
            }}
            className="btn-theme-primary px-4 py-3 sm:py-2 rounded-lg w-full sm:w-auto"
          >
            + Log Training
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {error}
            <button onClick={() => setError('')} className="float-right font-bold">×</button>
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm">
            {success}
            <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
          </div>
        )}

        {/* Summary Stats */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-2">
            <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">
              Total on water KM this {onWaterPeriod}
            </p>
            <p className="text-2xl font-bold text-theme-primary">{onWaterKm.toFixed(1)} km</p>
            <div className="mt-2 flex gap-2">
              {['week', 'month', 'year'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setOnWaterPeriod(p)}
                  className={`px-3 py-1.5 text-xs rounded-full border ${
                    onWaterPeriod === p
                      ? 'bg-theme-primary text-white border-theme-primary'
                      : 'bg-white text-blue-700 border-blue-200'
                  }`}
                >
                  {p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'Year'}
                </button>
              ))}
            </div>
          </div>
          {/* Total */}
          <div className="bg-theme-bg-secondary p-3 rounded-lg text-center mb-2">
            <p className="text-2xl font-bold text-theme-primary">{formatMinutes(totalMinutes)}</p>
            <p className="text-xs text-gray-600">Total Training</p>
          </div>
          {/* Activity breakdown - single row */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {MODALITIES.map((mod) => (
              <div key={mod} className={`flex-1 min-w-0 p-2 rounded-lg text-center border ${MODALITY_COLORS[mod]}`}>
                <p className="text-sm font-bold truncate">{formatMinutes(totalByModality[mod] || 0)}</p>
                <p className="text-xs truncate">{MODALITY_ICONS[mod]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base flex-1 sm:flex-initial sm:w-40"
        >
          <option value="all">All Time</option>
          <option value="week">Past Week</option>
          <option value="month">Past Month</option>
        </select>
        <select
          value={filterModality}
          onChange={(e) => setFilterModality(e.target.value)}
          className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base flex-1 sm:flex-initial sm:w-40"
        >
          <option value="">All Activities</option>
          {MODALITIES.map((mod) => (
            <option key={mod} value={mod}>{MODALITY_ICONS[mod]} {mod}</option>
          ))}
        </select>
      </div>

      {/* Log Entries */}
      {logs.length === 0 ? (
        <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
          <p className="text-lg mb-2">No training logs yet</p>
          <p className="text-sm">Tap "Log Training" to record your first workout!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div
              key={log.id}
              className="border rounded-lg p-4 bg-white shadow-sm active:bg-gray-50 transition-all"
            >
              <div className="flex flex-col gap-3">
                {/* Top Row: Badge and Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  {/* Modality Badge */}
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${MODALITY_COLORS[log.modality]} w-fit`}>
                    <span className="text-base">{MODALITY_ICONS[log.modality]}</span>
                    <span>{log.modality}</span>
                  </div>
                  {log.source === 'strava' && (
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200 w-fit">
                      From Strava
                    </span>
                  )}

                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xl font-bold text-theme-primary">
                        {formatMinutes(log.minutes)}
                      </span>
                      {log.distance_meters && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-lg font-semibold text-gray-700">
                            {formatDistance(log.distance_meters)}
                          </span>
                        </>
                      )}
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{formatDate(log.log_date)}</span>
                    </div>
                    {log.notes && (
                      <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">{log.notes}</p>
                    )}
                    {(log.equipment_name || log.equipment_id) && (
                      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                        <p className="text-sm font-semibold text-theme-primary">
                          Equipment: {log.equipment_name || `ID ${log.equipment_id}`}
                          {log.equipment_class ? (
                            <span className="text-gray-600 font-normal"> ({log.equipment_class})</span>
                          ) : null}
                        </p>
                        {log.distance_meters ? (
                          <p className="text-sm text-gray-600">
                            {formatDistance(log.distance_meters)} was added to this equipment&apos;s meter log.
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">Linked to this equipment&apos;s meter log.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions - Full width on mobile */}
                <div className="flex gap-2 pt-2 border-t sm:border-t-0 sm:pt-0 sm:justify-end">
                  <button
                    onClick={() => handleEdit(log)}
                    className="text-blue-600 hover:text-blue-800 active:bg-blue-100 px-4 py-2.5 text-sm border border-blue-200 rounded-lg hover:bg-blue-50 flex-1 sm:flex-initial min-h-[44px] font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(log.id)}
                    className="text-red-600 hover:text-red-800 active:bg-red-100 px-4 py-2.5 text-sm border border-red-200 rounded-lg hover:bg-red-50 flex-1 sm:flex-initial min-h-[44px] font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <MobileModal
        isOpen={showAddForm}
        onClose={resetForm}
        title={editingLog ? 'Edit Training Log' : 'Log Training'}
        maxWidth="max-w-md"
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Date</label>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border p-3 rounded w-full text-base"
              required
            />
          </div>

          {/* Team plan prefill */}
          {!editingLog && planEntries.length > 0 && (
            <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-800">📋 From team plan</span>
                {appliedPlanEntryId && (
                  <button
                    type="button"
                    onClick={() => setAppliedPlanEntryId(null)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Unlink from plan
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {planEntries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => applyPlanEntry(entry)}
                    className={`w-full text-left px-3 py-2 rounded border text-sm transition-colors ${
                      appliedPlanEntryId === entry.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-800 border-blue-200 hover:border-blue-400'
                    }`}
                  >
                    <span className="font-medium">
                      {planEntries.length > 1 ? `Session ${entry.sessionNumber}: ` : ''}{planEntrySummary(entry)}
                    </span>
                    {entry.squadName && (
                      <span className={`ml-2 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        appliedPlanEntryId === entry.id ? 'bg-blue-500 text-blue-50' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.squadName}
                      </span>
                    )}
                    {entry.title && (
                      <span className={`block text-xs ${appliedPlanEntryId === entry.id ? 'text-blue-100' : 'text-gray-500'}`}>
                        {entry.title}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-blue-700 mt-2">
                {appliedPlanEntryId
                  ? 'Prefilled from the plan — everything below is still editable.'
                  : 'Tap a planned session to prefill this log.'}
              </p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Activity Type</label>
            <select
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              className={`border p-3 rounded w-full text-base ${
                modality ? MODALITY_COLORS[modality] : 'bg-white'
              }`}
              required
            >
              <option value="">Select activity...</option>
              {MODALITIES.map((mod) => (
                <option key={mod} value={mod}>
                  {MODALITY_ICONS[mod]} {mod}
                </option>
              ))}
            </select>
          </div>

          {!zoneLoggingEnabled && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Duration (minutes)</label>
              <input
                type="number"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="e.g., 60"
                min="1"
                className="border p-3 rounded w-full text-base"
                required
              />
              {minutes && (
                <p className="text-sm text-gray-500 mt-1">= {formatMinutes(parseInt(minutes) || 0)}</p>
              )}
            </div>
          )}

          {/* Equipment Selection - Optional, for logging meters to equipment */}
          {(modality === 'Erg' || modality === 'Row') && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Equipment <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {(() => {
                const byModality = equipmentList.filter(eq =>
                  modality === 'Erg' ? eq.boat_class === 'Erg' : tracksMeters(eq.boat_class)
                );
                if (byModality.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded">
                      No {modality === 'Erg' ? 'ergs' : 'boats'} available. Add equipment in the Equipment tab.
                    </p>
                  );
                }
                const q = equipmentSearch.trim().toLowerCase();
                const filteredEquipment = byModality.filter(eq =>
                  !q ||
                  eq.name.toLowerCase().includes(q) ||
                  (eq.boat_class || '').toLowerCase().includes(q)
                );
                const selectedEq = byModality.find(eq => String(eq.id) === String(selectedEquipment));
                return (
                  <div className="relative">
                    <div className="flex items-center border rounded bg-white">
                      <input
                        type="text"
                        value={equipmentSearch}
                        onChange={(e) => { setEquipmentSearch(e.target.value); setShowEquipmentDropdown(true); }}
                        onFocus={() => setShowEquipmentDropdown(true)}
                        onBlur={() => setTimeout(() => setShowEquipmentDropdown(false), 150)}
                        placeholder={selectedEq ? `${selectedEq.name} (${selectedEq.boat_class})` : 'Search equipment…'}
                        className="flex-1 p-3 rounded text-base bg-white outline-none"
                      />
                      {selectedEq && !equipmentSearch && (
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); setSelectedEquipment(''); }}
                          className="px-3 text-gray-400 hover:text-gray-600 text-lg leading-none"
                          aria-label="Clear equipment"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {showEquipmentDropdown && (
                      <div className="absolute z-20 mt-1 w-full border rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); setSelectedEquipment(''); setEquipmentSearch(''); setShowEquipmentDropdown(false); }}
                          className={`block w-full text-left px-3 py-2 text-sm border-b hover:bg-gray-50 ${!selectedEquipment ? 'bg-blue-50' : ''}`}
                        >
                          No equipment selected
                        </button>
                        {filteredEquipment.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-gray-500 italic">No matches for “{equipmentSearch}”.</p>
                        ) : (
                          filteredEquipment.map((eq) => (
                            <button
                              key={eq.id}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); setSelectedEquipment(String(eq.id)); setEquipmentSearch(''); setShowEquipmentDropdown(false); }}
                              className={`block w-full text-left px-3 py-2 text-sm border-b hover:bg-gray-50 ${String(eq.id) === String(selectedEquipment) ? 'bg-blue-50 font-medium' : ''}`}
                            >
                              {eq.name} <span className="text-gray-500">({eq.boat_class})</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {selectedEquipment && (
                <p className="text-xs text-blue-700 mt-1 font-medium">
                  Meters will be written to this equipment log (duplicate-day check applies).
                </p>
              )}
              {selectedEquipment && (() => {
                const eq = equipmentList.find((e) => String(e.id) === String(selectedEquipment));
                const cap = eq ? getSeatCountForBoatClass(eq.boat_class) : 8;
                return (
                  <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                    <label className="block text-sm font-semibold mb-1">
                      Credit meters to athletes (optional)
                      {meterCreditUserIds.length > 0 && (
                        <span className="text-blue-700 font-normal"> ({meterCreditUserIds.length} selected)</span>
                      )}
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Leave empty to credit only yourself. Max {cap} for this {eq?.boat_class || 'equipment'}.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowMeterCreditSelector((v) => !v)}
                      className="w-full border rounded-lg px-3 py-2 text-left text-sm bg-white flex justify-between items-center"
                    >
                      <span>
                        {meterCreditUserIds.length === 0
                          ? '— Default: you only —'
                          : `${meterCreditUserIds.length} athlete${meterCreditUserIds.length > 1 ? 's' : ''} selected`}
                      </span>
                      <span className="text-gray-400">{showMeterCreditSelector ? '▲' : '▼'}</span>
                    </button>
                    {showMeterCreditSelector && (
                      <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto bg-white">
                        <div className="flex justify-between px-2 py-1.5 border-b text-xs bg-gray-50">
                          <button
                            type="button"
                            className="text-blue-700"
                            onClick={() =>
                              setMeterCreditUserIds(
                                clubMembersForMeters.slice(0, cap).map((m) => String(m.id))
                              )
                            }
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="text-red-600"
                            onClick={() => setMeterCreditUserIds([])}
                          >
                            Clear
                          </button>
                        </div>
                        {clubMembersForMeters.map((m) => {
                          const idStr = String(m.id);
                          const checked = meterCreditUserIds.includes(idStr);
                          return (
                            <label
                              key={m.id}
                              className={`flex items-center gap-2 px-3 py-2 text-sm border-b cursor-pointer ${
                                checked ? 'bg-blue-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  if (checked) {
                                    setMeterCreditUserIds((prev) => prev.filter((x) => x !== idStr));
                                  } else if (meterCreditUserIds.length < cap) {
                                    setMeterCreditUserIds((prev) => [...prev, idStr]);
                                  }
                                }}
                              />
                              <span>{m.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Distance (optional)</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                placeholder="e.g., 10"
                min="0"
                step="any"
                inputMode="decimal"
                className="border p-3 rounded flex-1 text-base"
              />
              <select
                value={distanceUnit}
                onChange={(e) => setDistanceUnit(e.target.value)}
                className="border p-3 rounded bg-white text-gray-800 text-base w-24"
              >
                <option value="meters">m</option>
                <option value="km">km</option>
              </select>
            </div>
            {distance && (
              <p className="text-sm text-gray-500 mt-1">
                = {distanceUnit === 'km' 
                    ? `${(parseFloat(distance) * 1000).toLocaleString()}m` 
                    : `${(parseFloat(distance) / 1000).toFixed(2)}km`}
              </p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 10k steady state, felt good"
              className="border p-3 rounded w-full text-base min-h-[80px]"
              rows={2}
            />
          </div>

          {zoneLoggingEnabled && (
            <div className="mb-6 p-3 rounded border bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold">Zone Minutes</label>
                <span className="text-xs text-gray-600">
                  Total: {sumZoneMinutes(actualZones)} min
                </span>
              </div>
              <div className="bg-white border rounded-lg p-3 overflow-x-auto">
                <div className="grid grid-cols-[minmax(90px,1fr)_96px_96px] sm:grid-cols-[minmax(120px,1fr)_110px_110px] gap-2 sm:gap-3 items-center mb-2">
                  <h5 className="text-sm font-semibold text-gray-700 text-center">Zone</h5>
                  <h5 className="text-sm font-semibold text-gray-700 text-center">Actual</h5>
                  <h5 className="text-sm font-semibold text-gray-700 text-center">Intended</h5>
                </div>
                <div className="space-y-2">
                  {zoneDefinitions.map(zone => (
                    <div key={zone.key} className="grid grid-cols-[minmax(90px,1fr)_96px_96px] sm:grid-cols-[minmax(120px,1fr)_110px_110px] gap-2 sm:gap-3 items-center">
                      <label className="text-sm text-gray-700 text-center break-words leading-tight px-1">{zone.label}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={actualZones[zone.key] || ''}
                        onChange={(e) =>
                          setActualZones(prev => ({
                            ...prev,
                            [zone.key]: e.target.value.replace(/[^0-9]/g, '')
                          }))
                        }
                        className="border p-2 rounded w-full text-sm text-center"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={intendedZones[zone.key] || ''}
                        onChange={(e) =>
                          setIntendedZones(prev => ({
                            ...prev,
                            [zone.key]: e.target.value.replace(/[^0-9]/g, '')
                          }))
                        }
                        className="border p-2 rounded w-full text-sm text-center"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              className="btn-theme-primary px-4 py-3 rounded-lg flex-1"
            >
              {editingLog ? 'Save Changes' : 'Log Training'}
            </button>
            {!editingLog && (
              <button
                type="button"
                onClick={handleSaveAndAddAnother}
                className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex-1"
              >
                Save & Add Another
              </button>
            )}
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </MobileModal>

      {/* Duplicate Detection Modal */}
      <MobileModal
        isOpen={showDuplicatePrompt}
        onClose={cancelDuplicatePrompt}
        title="Existing Meters Logged"
        maxWidth="max-w-md"
        zIndex={70}
      >
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 text-sm">
              <span className="font-semibold">
                {existingLogs.every(log => log.is_own_log)
                  ? "You've already logged meters"
                  : "Meters have been logged"}
              </span> for this equipment on {pendingSubmission?.payload?.logDate}:
            </p>
            <div className="mt-3 space-y-2">
              {existingLogs.map((log, idx) => (
                <div key={idx} className={`rounded p-2 text-sm ${log.is_own_log ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`}>
                  <span className="font-medium">
                    {log.is_own_log ? 'You' : log.logger_name}
                  </span> logged{' '}
                  <span className="font-semibold text-blue-600">{Number(log.meters || 0).toLocaleString()}m</span>
                  {log.is_own_log && <span className="text-amber-600 text-xs ml-2">(your log)</span>}
                  {log.notes && <span className="text-gray-500 block text-xs mt-1">"{log.notes}"</span>}
                </div>
              ))}
            </div>
          </div>

          <p className="text-gray-700 mb-4 text-center">
            {existingLogs.every(log => log.is_own_log)
              ? <>Did you train <strong>again</strong> on this equipment today?</>
              : <>Is this the <strong>same outing</strong> (e.g., same crew)?</>}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => handleDuplicateChoice(true)}
              className="w-full bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 border border-gray-300"
            >
              <span className="block font-semibold">
                {existingLogs.every(log => log.is_own_log)
                  ? "Oops, already logged"
                  : "Yes, same session"}
              </span>
              <span className="text-xs text-gray-500">Log to my personal record only (boat total unchanged)</span>
            </button>
            <button
              onClick={() => handleDuplicateChoice(false)}
              className="w-full bg-theme-primary text-white px-4 py-3 rounded-lg font-medium hover:opacity-90"
            >
              <span className="block font-semibold">
                {existingLogs.every(log => log.is_own_log)
                  ? "Yes, different outing"
                  : "No, different outing"}
              </span>
              <span className="text-xs text-white/80">Add my meters to the boat's total</span>
            </button>
            <button
              onClick={cancelDuplicatePrompt}
              className="w-full text-gray-500 px-4 py-2 text-sm hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </MobileModal>

      <MobileModal
        isOpen={Boolean(saveConflict)}
        onClose={dismissSaveConflict}
        title={saveConflict?.kind === 'duplicate' ? 'Already logged from Strava?' : 'Meters already credited'}
        maxWidth="max-w-md"
        zIndex={80}
      >
        <div>
          <p className="text-gray-800 mb-3">
            {saveConflict?.kind === 'duplicate'
              ? 'Your training log already has a Strava activity that looks like this session:'
              : 'Some selected athletes already have personal meters for this date:'}
          </p>
          <ul className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 space-y-1 text-sm text-amber-900">
            {(saveConflict?.lines || []).map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
          <p className="text-gray-600 text-sm mb-4">
            {saveConflict?.kind === 'duplicate'
              ? 'Your entry has not been saved yet. If this is the same session, choose "Don\'t save" so it isn\'t counted twice.'
              : 'Your entry has not been saved yet. Add the credit anyway, or go back and change the selection.'}
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={confirmSaveConflict}
              className="w-full bg-theme-primary text-white px-4 py-3 rounded-lg font-semibold hover:opacity-90"
            >
              {saveConflict?.kind === 'duplicate' ? 'Different session, save it' : 'Add credit anyway'}
            </button>
            <button
              type="button"
              onClick={dismissSaveConflict}
              className="w-full bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 border border-gray-300"
            >
              {saveConflict?.kind === 'duplicate' ? "Same session, don't save" : "Don't save"}
            </button>
          </div>
        </div>
      </MobileModal>
      </div>
    </div>
  );
}

